'use client'

import { cn } from '@/utilities/ui'
import { type VariantProps, cva } from 'class-variance-authority'
import * as React from 'react'

/**
 * CTA Button Variants
 *
 * | Variant              | Background    | Text  | Circle       | Hover Effect                    |
 * |----------------------|---------------|-------|--------------|----------------------------------|
 * | green                | Lime #00FF6F  | Black | Black filled | Black fills bg, text→white      |
 * | black                | Black         | White | Lime filled  | Lime fills bg, text→black       |
 * | blackOutline         | Transparent   | Black | Black border | Black fills bg, text→white      |
 * | blackOutlineMinimal  | Transparent   | Black | No circle    | Black fills bg, text→white      |
 * | whiteOutline         | Transparent   | White | White border | White fills bg, text→black      |
 * | white                | White         | Black | Black filled | Black fills bg, text→white      |
 */

export type CTAButtonVariant = 'green' | 'black' | 'blackOutline' | 'blackOutlineMinimal' | 'whiteOutline' | 'white'

const ctaButtonVariants = cva(
  // Base styles - responsive sizing with gap between text and arrow
  'relative inline-flex items-center justify-between rounded-full font-sans overflow-hidden group transition-colors py-0.5 pl-6 pr-0.5 gap-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 disabled:cursor-not-allowed active:scale-[0.98] motion-reduce:transition-none',
  {
    variants: {
      variant: {
        green: 'bg-civo-lime text-civo-black',
        black: 'bg-civo-black text-white',
        blackOutline: 'bg-transparent border border-civo-black text-civo-black',
        blackOutlineMinimal: 'bg-transparent border border-civo-black text-civo-black',
        whiteOutline: 'bg-transparent border border-white text-white',
        white: 'bg-white text-civo-black',
      },
    },
    defaultVariants: {
      variant: 'green',
    },
  },
)

// Arrow icon component - 8x8 from Figma design
const ArrowIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    width="8"
    height="8"
    viewBox="0 0 8 8"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={cn('size-2', className)}
    aria-hidden="true"
  >
    <path
      d="M4 8L3.2875 7.3L6.0875 4.5H0V3.5H6.0875L3.2875 0.7L4 0L8 4L4 8Z"
      fill="currentColor"
    />
  </svg>
)

// Download icon - 18x18 from Figma design
const DownloadIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 18 18"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={cn('size-[18px]', className)}
    aria-hidden="true"
  >
    <path
      d="M14.625 13.5V15.75H3.375V13.5H2.25V15.75C2.25 16.0484 2.36853 16.3345 2.5795 16.5455C2.79048 16.7565 3.07663 16.875 3.375 16.875H14.625C14.9234 16.875 15.2095 16.7565 15.4205 16.5455C15.6315 16.7565 15.75 16.0484 15.75 15.75V13.5H14.625ZM14.625 7.875L13.8319 7.08188L9.5625 11.3456V1.125H8.4375V11.3456L4.16812 7.08188L3.375 7.875L9 13.5L14.625 7.875Z"
      fill="currentColor"
    />
  </svg>
)

export type CTAButtonIconVariant = 'arrow' | 'download' | 'down'

const renderIcon = (iconVariant: CTAButtonIconVariant) => {
  if (iconVariant === 'download') return <DownloadIcon />
  if (iconVariant === 'down') return <ArrowIcon className="rotate-90" />
  return <ArrowIcon />
}

// Hover overlay background color per variant
const hoverOverlayBg: Record<string, string> = {
  green: 'bg-civo-black',
  black: 'bg-civo-lime',
  blackOutline: 'bg-civo-black',
  blackOutlineMinimal: 'bg-civo-black',
  whiteOutline: 'bg-white',
  white: 'bg-civo-black',
}

type CTAButtonSurface = 'black' | 'lime'

// Colour the hover overlay paints, to compare against the surface the button sits on
const hoverFill: Record<CTAButtonVariant, CTAButtonSurface | 'white'> = {
  green: 'black',
  black: 'lime',
  blackOutline: 'black',
  blackOutlineMinimal: 'black',
  whiteOutline: 'white',
  white: 'black',
}

const surfaceHoverOutline: Record<CTAButtonSurface, string> = {
  black: 'hover:outline hover:outline-1 hover:outline-white',
  lime: 'hover:outline hover:outline-1 hover:outline-civo-black',
}

const BLOCKED_PROTOCOLS = new Set(['javascript:', 'data:', 'vbscript:'])

// URL parsing strips the whitespace, case and embedded newlines a startsWith check would miss
const isSafeHref = (href: string): boolean => {
  try {
    return !BLOCKED_PROTOCOLS.has(new URL(href, window.location.href).protocol)
  } catch {
    return false
  }
}

// Text classes based on variant (with hover state)
const textClasses: Record<string, string> = {
  green: 'text-civo-black group-hover:text-white',
  black: 'text-white group-hover:text-civo-black',
  blackOutline: 'text-civo-black group-hover:text-white',
  blackOutlineMinimal: 'text-civo-black group-hover:text-white',
  whiteOutline: 'text-white group-hover:text-civo-black',
  white: 'text-civo-black group-hover:text-white',
}

// Arrow classes based on variant (with hover state)
const arrowClasses: Record<string, string> = {
  green: 'text-white',
  black: 'text-civo-black',
  blackOutline: 'text-civo-black group-hover:text-white',
  blackOutlineMinimal: 'text-civo-black group-hover:text-white',
  whiteOutline: 'text-white group-hover:text-civo-black',
  white: 'text-white',
}

export interface CTAButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof ctaButtonVariants> {
  children: React.ReactNode
  href?: string
  newTab?: boolean
  className?: string
  /** @deprecated Use variant prop with appropriate value instead */
  heroBackground?: 'black' | 'lime' | 'beige' | 'white'
  showArrow?: boolean
  iconVariant?: CTAButtonIconVariant
  // Background behind the button; outlines the pill on hover when the overlay would match it
  surface?: CTAButtonSurface
}

const CTAButton = React.forwardRef<HTMLButtonElement, CTAButtonProps>(
  (
    {
      className,
      variant = 'green',
      children,
      href,
      newTab,
      heroBackground,
      showArrow = true,
      iconVariant = 'arrow',
      surface,
      onClick,
      ...props
    },
    ref,
  ) => {
    const handleClick = (e: React.MouseEvent<HTMLButtonElement>): void => {
      if (onClick) {
        onClick(e)
      }

      if (href && !e.defaultPrevented && isSafeHref(href)) {
        if (newTab) {
          window.open(href, '_blank', 'noopener,noreferrer')
        } else {
          window.location.href = href
        }
      }
    }

    // Handle legacy heroBackground prop - automatically adjust variant for dark backgrounds
    let effectiveVariant = variant ?? 'green'
    if (heroBackground === 'black') {
      if (effectiveVariant === 'blackOutline') {
        effectiveVariant = 'whiteOutline' // Switch to white outline on black bg
      }
    }

    // Padding adjustment when arrow is hidden
    const paddingClass = showArrow ? '' : 'pr-6'
    const hoverOutlineClass =
      surface && hoverFill[effectiveVariant] === surface ? surfaceHoverOutline[surface] : ''

    const renderHoverOverlay = () => (
      <span
        className={cn(
          'absolute right-0 top-0 bottom-0 rounded-full',
          'w-0',
          'transition-all duration-300 motion-reduce:transition-none',
          'group-hover:w-full group-hover:rounded-full',
          hoverOverlayBg[effectiveVariant],
        )}
      />
    )

    return (
      <button
        className={cn(
          ctaButtonVariants({ variant: effectiveVariant, className }),
          paddingClass,
          hoverOutlineClass,
        )}
        ref={ref}
        onClick={handleClick}
        {...props}
      >
        {/* Hover overlay - expands from left side */}
        {renderHoverOverlay()}

        {/* Text content */}
        <span
          className={cn(
            'relative z-10 body-copy-medium font-medium transition-colors duration-300 motion-reduce:transition-none',
            textClasses[effectiveVariant] ?? '',
          )}
        >
          {children}
        </span>

        {/* Arrow section */}
        {showArrow && (
          <span
            className={cn(
              'relative z-10 shrink-0 rounded-full flex items-center justify-center overflow-hidden transition-colors duration-300 motion-reduce:transition-none',
              'size-11',
              arrowClasses[effectiveVariant] ?? '',
              // Circle background/border based on variant
              effectiveVariant === 'green' && 'bg-civo-black border border-civo-black group-hover:border-white',
              effectiveVariant === 'black' && 'bg-civo-lime border border-transparent group-hover:border-civo-black',
              effectiveVariant === 'blackOutline' && 'border border-civo-black group-hover:border-white',
              effectiveVariant === 'blackOutlineMinimal' && 'border border-transparent',
              effectiveVariant === 'whiteOutline' && 'border border-white group-hover:border-civo-black',
              effectiveVariant === 'white' && 'bg-civo-black border border-civo-black group-hover:border-white',
            )}
          >
            {/* Dual icon animation - slides right on hover */}
            <span className="relative block overflow-hidden">
              {/* First icon - visible by default, slides out to the right on hover */}
              <span className="block transition-transform duration-300 motion-reduce:transition-none group-hover:translate-x-[300%] motion-reduce:group-hover:translate-x-0">
                {renderIcon(iconVariant)}
              </span>
              {/* Second icon - hidden to the left, slides in on hover with slight delay */}
              <span
                className="absolute inset-0 block transition-transform duration-300 delay-75 motion-reduce:transition-none -translate-x-[300%] group-hover:translate-x-0 motion-reduce:translate-x-0"
                aria-hidden="true"
              >
                {renderIcon(iconVariant)}
              </span>
            </span>
          </span>
        )}
      </button>
    )
  },
)

CTAButton.displayName = 'CTAButton'

export { CTAButton, ctaButtonVariants, ArrowIcon, DownloadIcon }
